import { createContext, useContext, useEffect } from 'react';
import {
  QueryChanelContextProps,
  QueryChanelData,
  QueryChanelKey,
  QueryChanelOptions,
  QueryChanelProviderProps,
} from './queryChanel.types';
import { queryClient } from '@/Root';

export const queryChanel = new BroadcastChannel('query-chanel');

const QueryChanelContext = createContext({} as QueryChanelContextProps);

const optionsDefault: QueryChanelOptions = {
  executeQueriesOnPost: true,
};

export const useQueryChanel = () => useContext(QueryChanelContext).postQuery;

export default function QueryChanelProvider(props: QueryChanelProviderProps) {
  useEffect(() => {
    queryChanel.onmessage = onMessage;

    return () => {
      queryChanel.close();
    };
  }, [queryChanel]);

  const onMessage = async ({ data }: { data: QueryChanelData }) => {
    await Promise.all(
      Object.keys(data).map(async (dataKey) => {
        const key = dataKey as QueryChanelKey;

        const queries = data[key];

        if (!queries?.length) return;

        switch (key) {
          case QueryChanelKey.invalidateQueries:
            await Promise.all(
              queries?.map((queryKey) =>
                queryClient.invalidateQueries(queryKey),
              ),
            );
            break;

          case QueryChanelKey.cancelQueries:
            await Promise.all(
              queries?.map((queryKey) => queryClient.cancelQueries(queryKey)),
            );
            break;

          case QueryChanelKey.refetchQueries:
            await Promise.all(
              queries?.map((queryKey) => queryClient.refetchQueries(queryKey)),
            );
            break;

          case QueryChanelKey.resetQueries:
            await Promise.all(
              queries?.map((queryKey) => queryClient.resetQueries(queryKey)),
            );
            break;

          case QueryChanelKey.removeQueries:
            queries?.map((queryKey) => queryClient.removeQueries(queryKey));
            break;

          default:
            [];
            break;
        }
      }),
    );
  };

  const postQuery = (queryKeys: QueryChanelData, options = optionsDefault) => {
    try {
      queryChanel?.postMessage(queryKeys);

      if (options?.executeQueriesOnPost) {
        onMessage({ data: queryKeys });
      }
    } catch {}
  };

  return (
    <QueryChanelContext.Provider value={{ postQuery }}>
      {props.children}
    </QueryChanelContext.Provider>
  );
}
